/**
 * Ariadne Service — Cardano blockchain attestation for Limestone
 * 
 * Self-contained: inlines the Ariadne SDK logic so no npm package is needed.
 * The SDK is unbuilt TypeScript in the repo, so we port it to plain JS here.
 * 
 * API: https://ariadne-cardano.vercel.app
 */

const API_URL = 'https://ariadne-cardano.vercel.app';
const NETWORK = 'mainnet';

// ============================================================
// Error class
// ============================================================

class AriadneError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'AriadneError';
    this.code = code;
  }
}

// ============================================================
// Fees
// ============================================================

let cachedFees = null;
let feesFetchedAt = 0;
const FEES_CACHE_MS = 5 * 60 * 1000; // 5 minutes

export async function fetchFees() {
  const now = Date.now();
  if (cachedFees && (now - feesFetchedAt) < FEES_CACHE_MS) {
    return cachedFees;
  }

  try {
    const res = await fetch(`${API_URL}/api/fees`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    cachedFees = await res.json();
    feesFetchedAt = now;
    return cachedFees;
  } catch (err) {
    console.error('Failed to fetch Ariadne fees:', err);
    // Fallback defaults if API is down
    return {
      adminFee: { lovelace: 1000000, ada: 1 },
      networkFee: { lovelace: 200000, ada: 0.2 },
      total: { lovelace: 1200000, ada: 1.2 },
      minimumWalletBalance: { lovelace: 3000000, ada: 3 }
    };
  }
}

// ============================================================
// Wallet helpers
// ============================================================

export function getAvailableWallets() {
  if (typeof window === 'undefined' || !window.cardano) return [];
  return Object.keys(window.cardano).filter(key => {
    const entry = window.cardano[key];
    return entry && typeof entry === 'object' && 'enable' in entry;
  });
}

export async function enableWallet(name) {
  if (typeof window === 'undefined' || !window.cardano) {
    throw new AriadneError('WALLET_NOT_CONNECTED', 'No Cardano wallets detected');
  }
  const entry = window.cardano[name];
  if (!entry) {
    throw new AriadneError('WALLET_NOT_CONNECTED', `Wallet "${name}" not found`);
  }
  return entry.enable();
}

async function getWalletAddress(wallet) {
  const usedAddresses = await wallet.getUsedAddresses();
  if (usedAddresses.length > 0) return usedAddresses[0];
  const unusedAddresses = await wallet.getUnusedAddresses();
  if (unusedAddresses.length > 0) return unusedAddresses[0];
  return wallet.getChangeAddress();
}

/**
 * Get wallet ADA balance in lovelace
 */
async function getWalletBalance(wallet) {
  try {
    const balanceCbor = await wallet.getBalance();
    // CIP-30 getBalance returns CBOR-encoded value
    // For ADA-only, it's a simple integer. For multi-asset, it's an array.
    // We parse the lovelace amount from the CBOR hex string.
    // Simple case: just a uint
    if (typeof balanceCbor === 'string') {
      // Try parsing as a simple CBOR unsigned integer
      // Most wallets return the total lovelace as a CBOR-encoded uint
      const bytes = hexToBytes(balanceCbor);
      return parseCborUint(bytes);
    }
    return null;
  } catch (err) {
    console.error('Error reading wallet balance:', err);
    return null;
  }
}

function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

function parseCborUint(bytes) {
  // CBOR major type 0 = unsigned integer
  const first = bytes[0];
  const major = first >> 5;
  const additional = first & 0x1f;
  
  if (major !== 0) {
    // Might be an array (multi-asset balance) — first element is lovelace
    if (major === 4) {
      // Array — skip array header, parse first element
      // This is a simplified parser for the common case
      return parseCborUint(bytes.slice(1));
    }
    return null;
  }
  
  if (additional < 24) return additional;
  if (additional === 24) return bytes[1];
  if (additional === 25) return (bytes[1] << 8) | bytes[2];
  if (additional === 26) return (bytes[1] << 24) | (bytes[2] << 16) | (bytes[3] << 8) | bytes[4];
  if (additional === 27) {
    // 64-bit — use BigInt then convert
    // eslint-disable-next-line no-undef
    let val = BigInt(0);
    for (let i = 1; i <= 8; i++) {
      // eslint-disable-next-line no-undef
      val = (val << BigInt(8)) | BigInt(bytes[i]); // eslint-disable-line no-undef
    }
    return Number(val);
  }
  return null;
}

// ============================================================
// API client
// ============================================================

async function apiFetch(path, options = {}) {
  const url = `${API_URL}${path}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const code = body.code || (res.status >= 500 ? 'API_UNAVAILABLE' : 'NETWORK_ERROR');
    throw new AriadneError(code, body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

async function apiBuildTx(params) {
  return apiFetch('/api/build-tx', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

async function apiSubmitTx(params) {
  return apiFetch('/api/submit-tx', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

async function apiVerify(txHash, content, network) {
  let url = `/api/verify?txHash=${encodeURIComponent(txHash)}&network=${network}`;
  if (content) url += `&content=${encodeURIComponent(content)}`;
  return apiFetch(url);
}

// ============================================================
// Ariadne client
// ============================================================

class Ariadne {
  constructor(config) {
    this.network = config.network || NETWORK;
    this.wallet = null;
  }

  connect(wallet) {
    this.wallet = wallet;
  }

  disconnect() {
    this.wallet = null;
  }

  /**
   * Check wallet balance against minimum required
   * Returns { sufficient, balance, required } or null if check fails
   */
  async checkBalance(minimumLovelace) {
    if (!this.wallet) return null;
    const balance = await getWalletBalance(this.wallet);
    if (balance === null) return null;
    return {
      sufficient: balance >= minimumLovelace,
      balance,
      required: minimumLovelace
    };
  }

  async attest(params) {
    if (!this.wallet) {
      throw new AriadneError('WALLET_NOT_CONNECTED', 'Call ariadne.connect(wallet) first');
    }
    if (!params.content || !params.entityId || !params.entityType) {
      throw new AriadneError('INVALID_PARAMS', 'content, entityId, and entityType are required');
    }

    // 1. Get wallet address
    const address = await getWalletAddress(this.wallet);

    // 2. Build unsigned tx via API
    const buildResult = await apiBuildTx({
      content: params.content,
      entityId: params.entityId,
      entityType: params.entityType,
      contentType: params.contentType,
      address,
      network: this.network,
    });

    // 3. Sign with wallet
    let witnessSet;
    try {
      witnessSet = await this.wallet.signTx(buildResult.unsignedTx, true);
    } catch (err) {
      throw new AriadneError('USER_REJECTED', 'Transaction signing was cancelled');
    }

    // 4. Submit via API
    const submitResult = await apiSubmitTx({
      unsignedTx: buildResult.unsignedTx,
      witnessSet,
      contentHash: buildResult.contentHash,
      timestamp: buildResult.timestamp,
      entityId: params.entityId,
      entityType: params.entityType,
      contentType: params.contentType,
      network: this.network,
    });

    return {
      txHash: submitResult.txHash,
      contentHash: buildResult.contentHash,
      timestamp: buildResult.timestamp,
      network: this.network,
    };
  }

  async verify(txHash, content) {
    return apiVerify(txHash, content, this.network);
  }
}

// ============================================================
// Limestone integration layer
// ============================================================

let ariadneInstance = null;
let connectedWalletName = null;

async function getAriadne(walletName) {
  if (ariadneInstance && connectedWalletName === walletName) {
    return ariadneInstance;
  }

  const wallet = await enableWallet(walletName);
  ariadneInstance = new Ariadne({ network: NETWORK });
  ariadneInstance.connect(wallet);
  connectedWalletName = walletName;

  return ariadneInstance;
}

export function resetAriadne() {
  ariadneInstance = null;
  connectedWalletName = null;
}

export function getStoredWalletName() {
  try {
    const saved = localStorage.getItem('limestone_wallet');
    if (saved) {
      const { walletName } = JSON.parse(saved);
      return walletName || null;
    }
  } catch (e) {
    console.error('Error reading wallet name:', e);
  }
  return null;
}

/**
 * Check wallet balance before attempting attestation.
 * Returns { sufficient, balanceAda, requiredAda } or null if check fails.
 */
export async function checkWalletBalance(walletName) {
  try {
    const ariadne = await getAriadne(walletName);
    const fees = await fetchFees();
    const result = await ariadne.checkBalance(fees.minimumWalletBalance.lovelace);
    if (!result) return null;
    return {
      sufficient: result.sufficient,
      balanceAda: result.balance / 1_000_000,
      requiredAda: fees.minimumWalletBalance.ada
    };
  } catch (err) {
    console.error('Balance check failed:', err);
    return null;
  }
}

export async function attestPost(walletName, post, entity) {
  const ariadne = await getAriadne(walletName);

  const contentToAttest = [
    post.title,
    post.subtitle || '',
    post.content
  ].filter(Boolean).join('\n\n');

  const proof = await ariadne.attest({
    content: contentToAttest,
    entityId: entity.id,
    entityType: entity.type === 'organization' ? 'organization' : 'individual',
    contentType: 'post'
  });

  return {
    txHash: proof.txHash,
    contentHash: proof.contentHash,
    timestamp: proof.timestamp
  };
}

export async function verifyPost(txHash, post) {
  const ariadne = new Ariadne({ network: NETWORK });

  const contentToVerify = [
    post.title,
    post.subtitle || '',
    post.content
  ].filter(Boolean).join('\n\n');

  return await ariadne.verify(txHash, contentToVerify);
}

export function getAttestErrorMessage(err) {
  switch (err.code) {
    case 'WALLET_NOT_CONNECTED':
      return 'Wallet disconnected. Please reconnect and try again.';
    case 'USER_REJECTED':
      return 'Transaction cancelled.';
    case 'INSUFFICIENT_FUNDS':
      return 'Not enough ADA in wallet. Attestation costs ~1.2 ADA (1 ADA service fee + ~0.2 ADA network fee). Minimum wallet balance: 3 ADA.';
    case 'API_UNAVAILABLE':
      return 'Attestation service is temporarily unavailable. Try again in a few minutes.';
    case 'TX_BUILD_FAILED':
      return 'Transaction could not be built. Your wallet may not have enough ADA or may be on the wrong network.';
    default:
      return 'Attestation failed. Please try again.';
  }
}